import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { FileText, Loader2, ShieldCheck, History } from 'lucide-react';
import { StatusChip, fmtDate } from './issueMeta';
import { apiErrorText, openVaultFile } from '@/lib/vaultUpload';

const API = process.env.REACT_APP_BACKEND_URL;

function UpdateRow({ u }) {
  return (
    <li className="relative pl-5 pb-4 border-l last:pb-0" data-testid={`issue-update-${u.id}`}>
      <span className="absolute -left-1.5 top-1 h-3 w-3 rounded-full border-2 border-background bg-foreground/40" />
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-semibold">{u.kind === 'verified' ? `Verified ${fmtDate(u.meeting_date)}` : `Meeting ${fmtDate(u.meeting_date)}`}</span>
        <StatusChip status={u.status} />
        {u.edited && <span className="text-[10px] text-muted-foreground">corrected by reviewer</span>}
      </div>
      <p className="text-sm mt-1">{u.statement}</p>
      {u.previous_deadline && u.deadline && (
        <p className="text-xs text-amber-800 dark:text-amber-300 mt-1">Deadline moved: {fmtDate(u.previous_deadline)} → {fmtDate(u.deadline)}</p>
      )}
      {!u.previous_deadline && (u.deadline || u.deadline_text) && (
        <p className="text-xs text-muted-foreground mt-1">Target: {fmtDate(u.deadline) || u.deadline_text}</p>
      )}
      {u.reported_done_on && <p className="text-xs text-muted-foreground mt-1">Reported done on {fmtDate(u.reported_done_on)}</p>}
      {u.next_step && <p className="text-xs text-muted-foreground mt-1">Next step: {u.next_step}</p>}
      {u.quote && <blockquote className="text-xs italic text-muted-foreground border-l-2 pl-2 mt-2">“{u.quote}”</blockquote>}
      {u.source && (
        <button type="button" onClick={() => openVaultFile(u.source.storage_path).catch(() => toast.error('Could not open the minutes'))}
          className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1 text-left" data-testid={`issue-update-source-${u.id}`}>
          <FileText className="h-3 w-3 shrink-0" /> {u.source.title}{u.section ? ` · §${String(u.section).replace(/^§/, '')}` : ''}
        </button>
      )}
    </li>
  );
}

function VerifyForm({ issueId, headers, onDone }) {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/post-oc/issues/${issueId}/verify`, { note }, { headers });
      toast.success('Marked as verified');
      onDone();
    } catch (e) { toast.error(apiErrorText(e, 'Could not save')); } finally { setSaving(false); }
  };
  return (
    <div className="rounded-md border p-3 space-y-2" data-testid="issue-verify-form">
      <Label>How was the resolution verified?</Label>
      <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="issue-verify-note"
        placeholder="e.g. AOP reps inspected on 24 Nov 2026 – all bays marked; photos in the vault" />
      <Button size="sm" onClick={submit} disabled={saving || note.trim().length < 10} data-testid="issue-verify-submit">
        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <ShieldCheck className="h-3.5 w-3.5 mr-1" />}Confirm verified
      </Button>
    </div>
  );
}

export default function IssueHistoryDialog({ issueId, canReview, headers, onOpenChange, onChanged }) {
  const [data, setData] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const load = () => axios.get(`${API}/api/post-oc/issues/${issueId}/history`, { headers })
    .then((r) => setData(r.data)).catch((e) => toast.error(apiErrorText(e, 'Could not load history')));
  useEffect(() => { if (issueId) { setData(null); setVerifying(false); load(); } }, [issueId]); // eslint-disable-line react-hooks/exhaustive-deps

  const issue = data?.issue;
  return (
    <Dialog open={!!issueId} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="issue-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><History className="h-4 w-4" />{issue?.title || 'Issue history'}</DialogTitle>
          <DialogDescription>Meeting-by-meeting record, newest first. Every entry links to its source.</DialogDescription>
        </DialogHeader>
        {!data ? <Loader2 className="h-5 w-5 animate-spin mx-auto my-6" /> : (
          <ol className="ml-1.5">{data.updates.map((u) => <UpdateRow key={u.id} u={u} />)}</ol>
        )}
        {verifying && <VerifyForm issueId={issueId} headers={headers} onDone={() => { load(); onChanged?.(); setVerifying(false); }} />}
        <DialogFooter>
          {canReview && issue && issue.status !== 'resolved_verified' && !verifying && (
            <Button variant="outline" onClick={() => setVerifying(true)} data-testid="issue-verify-open">
              <ShieldCheck className="h-4 w-4 mr-1" /> Mark verified
            </Button>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="issue-history-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
